import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  Chip,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Collapse,
  Tooltip,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Language as GlobalIcon,
  Router as LocalIcon,
  Bookmark as ReservedIcon,
  Computer as DeviceIcon,
  CheckCircle as FreeIcon,
  Cancel as ReleaseIcon
} from '@mui/icons-material';
import axios from '../api/axios';

function Subnets() {
  const [subnets, setSubnets] = useState([]);
  const [interfaces, setInterfaces] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [expandedSubnet, setExpandedSubnet] = useState(null);
  const [subnetIps, setSubnetIps] = useState({});
  const [ipFilter, setIpFilter] = useState('all'); // all, free, reserved, assigned
  const [openSubnetDialog, setOpenSubnetDialog] = useState(false);
  const [openIpDialog, setOpenIpDialog] = useState(false);
  const [editingSubnet, setEditingSubnet] = useState(null);
  const [selectedIp, setSelectedIp] = useState(null);
  const [selectedSubnetId, setSelectedSubnetId] = useState(null);
  const [subnetFormData, setSubnetFormData] = useState({
    name: '',
    start_ip: '',
    end_ip: '',
    is_local: false,
    vlan: '',
    comment: ''
  });
  const [ipFormData, setIpFormData] = useState({
    interface_id: null,
    is_reserved: false,
    comment: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadSubnets();
    loadInterfaces();
  }, []);

  const loadSubnets = async () => {
    try {
      const response = await axios.get('/subnets/');
      setSubnets(response.data);
    } catch (err) {
      setError('Ошибка загрузки подсетей: ' + (err.response?.data?.detail || err.message));
    }
  };

  const loadInterfaces = async () => {
    try {
      const response = await axios.get('/devices/?page_size=1000');
      const allInterfaces = [];
      for (const device of response.data.items || []) {
        if (device.interfaces) {
          for (const iface of device.interfaces) {
            allInterfaces.push({
              ...iface,
              device_name: device.name,
              device_id: device.id
            });
          }
        }
      }
      setInterfaces(allInterfaces);
    } catch (err) {
      console.error('Error loading interfaces:', err);
    }
  };

  const loadSubnetIps = async (subnetId) => {
    try {
      const response = await axios.get(`/subnets/${subnetId}/ips`);
      setSubnetIps(prev => ({
        ...prev,
        [subnetId]: response.data
      }));
    } catch (err) {
      console.error('Error loading IPs:', err);
    }
  };

  const handleExpandSubnet = (subnetId) => {
    if (expandedSubnet === subnetId) {
      setExpandedSubnet(null);
    } else {
      setExpandedSubnet(subnetId);
      loadSubnetIps(subnetId);
    }
  };

  // Subnet dialog handlers
  const handleOpenSubnetDialog = (subnet = null) => {
    if (subnet) {
      setEditingSubnet(subnet);
      setSubnetFormData({
        name: subnet.name,
        start_ip: subnet.start_ip || '',
        end_ip: subnet.end_ip || '',
        is_local: subnet.is_local,
        vlan: subnet.vlan || '',
        comment: subnet.comment || ''
      });
    } else {
      setEditingSubnet(null);
      setSubnetFormData({
        name: '',
        start_ip: '',
        end_ip: '',
        is_local: tabValue === 1,
        vlan: '',
        comment: ''
      });
    }
    setOpenSubnetDialog(true);
    setError('');
  };

  const handleCloseSubnetDialog = () => {
    setOpenSubnetDialog(false);
    setEditingSubnet(null);
    setError('');
  };

  const handleSubmitSubnet = async () => {
    try {
      setLoading(true);
      setError('');

      const data = {
        ...subnetFormData,
        start_ip: subnetFormData.start_ip || null,
        end_ip: subnetFormData.end_ip || null,
        vlan: subnetFormData.vlan || null,
        comment: subnetFormData.comment || null
      };

      if (editingSubnet) {
        await axios.put(`/subnets/${editingSubnet.id}`, data);
      } else {
        await axios.post('/subnets/', data);
      }

      handleCloseSubnetDialog();
      loadSubnets();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSubnet = async (id) => {
    if (!window.confirm('Вы уверены? Все IP адреса в этой подсети будут удалены!')) {
      return;
    }

    try {
      await axios.delete(`/subnets/${id}`);
      loadSubnets();
      if (expandedSubnet === id) {
        setExpandedSubnet(null);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  // IP dialog handlers - now opens when clicking on any IP row
  const handleOpenIpDialog = (subnetId, ip) => {
    setSelectedSubnetId(subnetId);
    setSelectedIp(ip);
    setIpFormData({
      interface_id: ip.interface_id,
      is_reserved: ip.is_reserved || false,
      comment: ip.comment || ''
    });
    setOpenIpDialog(true);
    setError('');
  };

  const handleCloseIpDialog = () => {
    setOpenIpDialog(false);
    setSelectedIp(null);
    setSelectedSubnetId(null);
    setError('');
  };

  const handleSubmitIp = async () => {
    try {
      setLoading(true);
      setError('');

      const data = {
        interface_id: ipFormData.interface_id || null,
        is_reserved: ipFormData.is_reserved,
        comment: ipFormData.comment || null
      };

      // Use POST with IP in URL path
      await axios.post(`/subnets/${selectedSubnetId}/ips/${selectedIp.ip}`, data);

      handleCloseIpDialog();
      loadSubnetIps(selectedSubnetId);
      loadSubnets();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReleaseIp = async (subnetId, ip) => {
    if (!window.confirm(`Освободить IP ${ip}?`)) {
      return;
    }

    try {
      await axios.delete(`/subnets/${subnetId}/ips/${ip}`);
      loadSubnetIps(subnetId);
      loadSubnets();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  // Filter subnets by tab
  const filteredSubnets = subnets.filter(s =>
    tabValue === 0 ? !s.is_local : s.is_local
  );

  // Filter IPs by status
  const getFilteredIps = (subnetId) => {
    const ips = subnetIps[subnetId] || [];
    if (ipFilter === 'all') return ips;
    return ips.filter(ip => ip.status === ipFilter);
  };

  const getUsagePercent = (subnet) => {
    if (subnet.is_local || !subnet.start_ip || !subnet.end_ip) return 0;
    const total = ipToInt(subnet.end_ip) - ipToInt(subnet.start_ip) + 1;
    return Math.round((subnet.ip_count / total) * 100);
  };

  const ipToInt = (ip) => {
    if (!ip) return 0;
    const parts = ip.split('.').map(Number);
    return ((parts[0] * 256 + parts[1]) * 256 + parts[2]) * 256 + parts[3];
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'free': return 'success';
      case 'reserved': return 'warning';
      case 'assigned': return 'info';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'free': return 'Свободен';
      case 'reserved': return 'Зарезервирован';
      case 'assigned': return 'Занят';
      default: return status;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Управление IP адресами</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenSubnetDialog()}
        >
          Добавить подсеть
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab
            icon={<GlobalIcon />}
            label={`Глобальные подсети (${subnets.filter(s => !s.is_local).length})`}
            iconPosition="start"
          />
          <Tab
            icon={<LocalIcon />}
            label={`Локальные подсети (${subnets.filter(s => s.is_local).length})`}
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={50}></TableCell>
              <TableCell>Название</TableCell>
              {tabValue === 0 && <TableCell>Диапазон IP</TableCell>}
              <TableCell>VLAN</TableCell>
              <TableCell>IP адреса</TableCell>
              {tabValue === 0 && <TableCell>Использование</TableCell>}
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSubnets.map((subnet) => (
              <React.Fragment key={subnet.id}>
                <TableRow
                  hover
                  sx={{ cursor: 'pointer', '& > *': { borderBottom: expandedSubnet === subnet.id ? 0 : undefined } }}
                  onClick={() => handleExpandSubnet(subnet.id)}
                >
                  <TableCell>
                    <IconButton size="small">
                      {expandedSubnet === subnet.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" fontWeight="bold">
                      {subnet.name}
                    </Typography>
                    {subnet.comment && (
                      <Typography variant="caption" color="text.secondary">
                        {subnet.comment}
                      </Typography>
                    )}
                  </TableCell>
                  {tabValue === 0 && (
                    <TableCell>
                      {subnet.start_ip && subnet.end_ip ? (
                        <Typography variant="body2" fontFamily="monospace">
                          {subnet.start_ip} - {subnet.end_ip}
                        </Typography>
                      ) : '-'}
                    </TableCell>
                  )}
                  <TableCell>
                    {subnet.vlan ? (
                      <Chip label={subnet.vlan} size="small" variant="outlined" />
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Занято / Зарезервировано">
                        <Chip
                          label={`${subnet.assigned_count + subnet.reserved_count}`}
                          size="small"
                          color="primary"
                        />
                      </Tooltip>
                      {subnet.assigned_count > 0 && (
                        <Tooltip title="Назначено устройствам">
                          <Chip
                            icon={<DeviceIcon />}
                            label={subnet.assigned_count}
                            size="small"
                            color="info"
                          />
                        </Tooltip>
                      )}
                      {subnet.reserved_count > 0 && (
                        <Tooltip title="Зарезервировано">
                          <Chip
                            icon={<ReservedIcon />}
                            label={subnet.reserved_count}
                            size="small"
                            color="warning"
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  {tabValue === 0 && (
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 100 }}>
                        <LinearProgress
                          variant="determinate"
                          value={getUsagePercent(subnet)}
                          sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                          color={getUsagePercent(subnet) > 80 ? 'warning' : 'primary'}
                        />
                        <Typography variant="caption">
                          {getUsagePercent(subnet)}%
                        </Typography>
                      </Box>
                    </TableCell>
                  )}
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenSubnetDialog(subnet)}
                      title="Редактировать"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteSubnet(subnet.id)}
                      title="Удалить"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>

                {/* Expanded IP list */}
                <TableRow>
                  <TableCell colSpan={tabValue === 0 ? 7 : 5} sx={{ py: 0 }}>
                    <Collapse in={expandedSubnet === subnet.id} timeout="auto" unmountOnExit>
                      <Box sx={{ py: 2, px: 3, bgcolor: 'grey.50' }}>
                        {/* Filter buttons */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="subtitle2">
                            IP адреса в подсети ({getFilteredIps(subnet.id).length})
                          </Typography>
                          <ToggleButtonGroup
                            size="small"
                            value={ipFilter}
                            exclusive
                            onChange={(e, v) => v && setIpFilter(v)}
                          >
                            <ToggleButton value="all">Все</ToggleButton>
                            <ToggleButton value="free">Свободные</ToggleButton>
                            <ToggleButton value="reserved">Зарезервированные</ToggleButton>
                            <ToggleButton value="assigned">Занятые</ToggleButton>
                          </ToggleButtonGroup>
                        </Box>

                        {/* IP table */}
                        <TableContainer sx={{ maxHeight: 400 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell>IP адрес</TableCell>
                                <TableCell>Статус</TableCell>
                                <TableCell>Устройство / Комментарий</TableCell>
                                <TableCell align="right">Действия</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {getFilteredIps(subnet.id).map((ip) => (
                                <TableRow
                                  key={ip.ip}
                                  hover
                                  sx={{
                                    cursor: 'pointer',
                                    bgcolor: ip.status === 'free' ? 'success.50' :
                                             ip.status === 'reserved' ? 'warning.50' :
                                             ip.status === 'assigned' ? 'info.50' : 'inherit'
                                  }}
                                  onClick={() => handleOpenIpDialog(subnet.id, ip)}
                                >
                                  <TableCell>
                                    <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
                                      {ip.ip}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      size="small"
                                      label={getStatusLabel(ip.status)}
                                      color={getStatusColor(ip.status)}
                                      icon={
                                        ip.status === 'free' ? <FreeIcon /> :
                                        ip.status === 'reserved' ? <ReservedIcon /> :
                                        <DeviceIcon />
                                      }
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {ip.device_name ? (
                                      <Typography variant="body2">
                                        {ip.device_name} ({ip.interface_name})
                                      </Typography>
                                    ) : ip.comment ? (
                                      <Typography variant="body2" color="text.secondary">
                                        {ip.comment}
                                      </Typography>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary">-</Typography>
                                    )}
                                  </TableCell>
                                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                    {ip.status !== 'free' && (
                                      <Tooltip title="Освободить">
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() => handleReleaseIp(subnet.id, ip.ip)}
                                        >
                                          <ReleaseIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                              {getFilteredIps(subnet.id).length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={4} align="center">
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                      {ipFilter === 'all'
                                        ? 'Нет IP адресов в этой подсети'
                                        : `Нет IP адресов со статусом "${getStatusLabel(ipFilter)}"`}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
            {filteredSubnets.length === 0 && (
              <TableRow>
                <TableCell colSpan={tabValue === 0 ? 7 : 5} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    {tabValue === 0
                      ? 'Нет глобальных подсетей. Глобальные подсети используют уникальные IP адреса с заданным диапазоном.'
                      : 'Нет локальных подсетей. Локальные подсети позволяют использовать одинаковые IP на разных устройствах.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Subnet Dialog */}
      <Dialog open={openSubnetDialog} onClose={handleCloseSubnetDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSubnet ? 'Редактировать подсеть' : 'Создать подсеть'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Название"
            value={subnetFormData.name}
            onChange={(e) => setSubnetFormData({ ...subnetFormData, name: e.target.value })}
            margin="normal"
            required
          />

          <FormControlLabel
            control={
              <Switch
                checked={subnetFormData.is_local}
                onChange={(e) => setSubnetFormData({ ...subnetFormData, is_local: e.target.checked })}
              />
            }
            label="Локальная подсеть (IP адреса могут повторяться)"
            sx={{ mt: 1, mb: 2 }}
          />

          {!subnetFormData.is_local && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Начальный IP"
                value={subnetFormData.start_ip}
                onChange={(e) => setSubnetFormData({ ...subnetFormData, start_ip: e.target.value })}
                margin="normal"
                placeholder="192.168.1.1"
                required={!subnetFormData.is_local}
              />
              <TextField
                fullWidth
                label="Конечный IP"
                value={subnetFormData.end_ip}
                onChange={(e) => setSubnetFormData({ ...subnetFormData, end_ip: e.target.value })}
                margin="normal"
                placeholder="192.168.1.254"
                required={!subnetFormData.is_local}
              />
            </Box>
          )}

          <TextField
            fullWidth
            label="VLAN (опционально)"
            value={subnetFormData.vlan}
            onChange={(e) => setSubnetFormData({ ...subnetFormData, vlan: e.target.value })}
            margin="normal"
            placeholder="VLAN 100"
          />

          <TextField
            fullWidth
            label="Комментарий"
            value={subnetFormData.comment}
            onChange={(e) => setSubnetFormData({ ...subnetFormData, comment: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSubnetDialog}>Отмена</Button>
          <Button
            onClick={handleSubmitSubnet}
            variant="contained"
            disabled={loading || !subnetFormData.name || (!subnetFormData.is_local && (!subnetFormData.start_ip || !subnetFormData.end_ip))}
          >
            {loading ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* IP Address Dialog */}
      <Dialog open={openIpDialog} onClose={handleCloseIpDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Настройка IP: {selectedIp?.ip}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={ipFormData.is_reserved}
                  onChange={(e) => setIpFormData({
                    ...ipFormData,
                    is_reserved: e.target.checked,
                    interface_id: e.target.checked ? null : ipFormData.interface_id
                  })}
                />
              }
              label="Зарезервировать"
            />
          </Box>

          {!ipFormData.is_reserved && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Назначить устройству</InputLabel>
              <Select
                value={ipFormData.interface_id || ''}
                onChange={(e) => setIpFormData({ ...ipFormData, interface_id: e.target.value || null })}
                label="Назначить устройству"
              >
                <MenuItem value="">
                  <em>Не назначен (свободен)</em>
                </MenuItem>
                {interfaces.map((iface) => (
                  <MenuItem key={iface.id} value={iface.id}>
                    {iface.device_name} - {iface.name} {iface.label ? `(${iface.label})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            fullWidth
            label={ipFormData.is_reserved ? "Причина резервирования (обязательно)" : "Комментарий"}
            value={ipFormData.comment}
            onChange={(e) => setIpFormData({ ...ipFormData, comment: e.target.value })}
            margin="normal"
            multiline
            rows={2}
            required={ipFormData.is_reserved}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseIpDialog}>Отмена</Button>
          <Button
            onClick={handleSubmitIp}
            variant="contained"
            disabled={loading || (ipFormData.is_reserved && !ipFormData.comment)}
          >
            {loading ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Subnets;
